/**
 * Deck utilities for creating, shuffling, and dealing cards
 */

import type { Card } from './types'

const RANKS = ['2', '3', '4', '5', '6', '7', '8', '9', 'T', 'J', 'Q', 'K', 'A'] as const
const SUITS = ['h', 'd', 'c', 's'] as const // hearts, diamonds, clubs, spades

/**
 * Create a standard 52-card deck
 * @returns Array of 52 cards (e.g., ["Ah", "Ad", "Ac", "As", "Kh", ...])
 */
export function createDeck(): Card[] {
  const deck: Card[] = []
  for (const rank of RANKS) {
    for (const suit of SUITS) {
      deck.push(`${rank}${suit}`)
    }
  }
  return deck
}

/**
 * Shuffle a deck using Fisher-Yates algorithm
 * Returns a NEW array (immutable)
 * @param cards - Deck to shuffle
 * @returns New shuffled array
 */
export function shuffle(cards: readonly Card[], rng: () => number = Math.random): Card[] {
  const shuffled = [...cards] // Create a copy

  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1))
    ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
  }

  return shuffled
}

/**
 * Deal cards from the top of the deck
 * Returns cards and remaining deck (immutable)
 * @param deck - Current deck
 * @param count - Number of cards to deal
 * @returns Object with dealt cards and remaining deck
 */
export function dealCards(
  deck: readonly Card[],
  count: number
): {
  cards: Card[]
  remaining: Card[]
} {
  if (count > deck.length) {
    throw new Error(`Cannot deal ${count} cards from deck with ${deck.length} cards`)
  }

  return {
    cards: deck.slice(0, count),
    remaining: deck.slice(count)
  }
}
